/**
 * P4 L0 加微信尝试记录弹框
 *
 * P1-3：截图 URL 字段改为文件上传（拖拽 / 点击 / 粘贴 三模式）
 */
import { useEffect, useRef, useState } from 'react';
import { Modal, Input, Radio, message } from 'antd';
import { useMutation } from '@tanstack/react-query';
import { $, api } from '../lib/api';
import { Btn } from './ui';

export function LogAttemptModal({
  open, customer, onClose, onDone,
}: {
  open: boolean;
  customer: any;
  onClose: () => void;
  onDone: () => void;
}) {
  const [form, setForm] = useState({
    attempt_method: 'redbook_dm',
    result: 'no_response',
    screenshot_url: '',
    notes: '',
  });
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) {
      setForm({ attempt_method: 'redbook_dm', result: 'no_response', screenshot_url: '', notes: '' });
    }
  }, [open]);

  const submit = useMutation({
    mutationFn: () => $.post(`/customers/${customer.id}/wechat-attempt`, form),
    onSuccess: () => { message.success('已记录尝试 +1'); onDone(); },
    onError: (e: any) => message.error(e?.message ?? '提交失败'),
  });

  // 上传文件 → 复用通用 /attachments 接口（或专用 wechat-attempt 子路由）
  const upload = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      message.error('请选择图片文件'); return;
    }
    if (file.size > 10 * 1024 * 1024) {
      message.error('图片不能超过 10MB'); return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('entityType', 'customer');
      fd.append('entityId', String(customer.id));
      fd.append('fieldName', 'wechat_attempt_screenshot');
      const r = await api.post('/attachments/upload', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = r.data?.url ?? r.data?.fileUrl ?? r.data?.publicUrl ?? r.data?.attachment?.url;
      if (!url) throw new Error('返回缺 url');
      setForm((f) => ({ ...f, screenshot_url: url }));
      message.success('截图已上传');
    } catch (e: any) {
      message.error(e?.response?.data?.message ?? e?.message ?? '上传失败');
    } finally {
      setUploading(false);
    }
  };

  // 全局粘贴（modal 打开期间）
  useEffect(() => {
    if (!open) return;
    const h = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const it of Array.from(items)) {
        if (it.type.startsWith('image/')) {
          const f = it.getAsFile();
          if (f) { e.preventDefault(); upload(new File([f], `paste-${Date.now()}.png`, { type: f.type })); return; }
        }
      }
    };
    window.addEventListener('paste', h);
    return () => window.removeEventListener('paste', h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Modal
      title="记录加微信尝试"
      open={open}
      onCancel={onClose}
      footer={
        <>
          <Btn kind="default" onClick={onClose}>取消</Btn>
          <Btn kind="primary" onClick={() => submit.mutate()} disabled={submit.isPending}>
            {submit.isPending ? '提交中…' : '保存'}
          </Btn>
        </>
      }
    >
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 12, marginBottom: 4 }}>方式</div>
        <Radio.Group value={form.attempt_method}
                     onChange={(e) => setForm({ ...form, attempt_method: e.target.value })}>
          <Radio value="redbook_dm">小红书私信</Radio>
          <Radio value="phone">电话</Radio>
          <Radio value="sms">短信</Radio>
          <Radio value="other">其它</Radio>
        </Radio.Group>
      </div>
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 12, marginBottom: 4 }}>结果</div>
        <Radio.Group value={form.result}
                     onChange={(e) => setForm({ ...form, result: e.target.value })}>
          <Radio value="no_response">未回复</Radio>
          <Radio value="rejected">拒绝</Radio>
          <Radio value="pending">已发待回</Radio>
        </Radio.Group>
      </div>
      {/* P1-3：URL 输入框改文件上传 */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 12, marginBottom: 4 }}>📷 上传截图（可选 - 作废申请时作为证据）</div>
        <div
          onClick={() => fileRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault(); setDragOver(false);
            const f = e.dataTransfer.files?.[0];
            if (f) upload(f);
          }}
          style={{
            padding: 16, borderRadius: 8, cursor: 'pointer',
            border: '1.5px dashed ' + (dragOver ? 'var(--primary)' : 'var(--border)'),
            background: dragOver ? 'rgba(51,112,255,0.06)' : 'var(--surface-hover, #fafafa)',
            textAlign: 'center', transition: 'all 120ms linear',
          }}>
          {form.screenshot_url ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <img src={form.screenshot_url} alt=""
                   style={{ width: 60, height: 80, objectFit: 'cover', borderRadius: 4 }} />
              <div style={{ flex: 1, textAlign: 'left' }}>
                <div style={{ fontSize: 13, color: 'var(--text-2)' }}>截图已上传 ✓</div>
                <div style={{ fontSize: 11, color: 'var(--text-3)', wordBreak: 'break-all' }}>
                  {form.screenshot_url.slice(0, 50)}…
                </div>
                <button onClick={(e) => { e.stopPropagation(); setForm({ ...form, screenshot_url: '' }); }}
                        style={{ marginTop: 4, fontSize: 11, color: 'var(--danger)', background: 'none', border: 'none', cursor: 'pointer' }}>
                  🗑️ 移除
                </button>
              </div>
            </div>
          ) : uploading ? (
            <div style={{ color: 'var(--primary)', fontSize: 13 }}>⏳ 上传中…</div>
          ) : (
            <>
              <div style={{ fontSize: 13, color: 'var(--text-2)' }}>
                点击 / 拖拽 / Ctrl+V 粘贴
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-3)', marginTop: 4 }}>
                支持 PNG / JPG / WEBP，最大 10MB
              </div>
            </>
          )}
          <input ref={fileRef} type="file" accept="image/*"
                 style={{ display: 'none' }}
                 onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ''; }} />
        </div>
      </div>
      <div>
        <div style={{ fontSize: 12, marginBottom: 4 }}>备注（选填）</div>
        <Input.TextArea value={form.notes}
                        onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
      </div>
    </Modal>
  );
}
